/**
 * gmailClient.js — Pure functions that read Gmail metadata.
 * No React dependencies. All errors propagate to callers.
 *
 * Requests go to our own backend's Gmail proxy, NOT to gmail.googleapis.com.
 * The browser authenticates with the TeamGrid JWT; the server attaches the
 * Google token and forwards. No Google credential ever reaches client code.
 * The fetch loop, paging, batching and backoff below are unchanged — only the
 * URL and the auth header differ.
 *
 * `memberId` selects whose mailbox to read: omit it for the caller's own,
 * pass a reportee's id for the team view. The server re-verifies reportee
 * access on every request.
 *
 * Listing strategy (gmail.metadata scope):
 *  - The `gmail.metadata` scope FORBIDS the `q=` parameter on messages.list
 *    (Google returns 403 PERMISSION_DENIED: "Metadata scope does not support 'q' parameter").
 *  - So we list by `labelIds` instead: one pass for INBOX, one pass for SENT.
 *  - We page through each label in reverse-chrono order, fetch metadata
 *    (which includes `internalDate`), filter to [from, to] client-side, and
 *    stop paging once an entire page is older than `from`.
 *  - Limitation: archived received mail (no INBOX label) is not counted.
 *    Most users keep recent mail in INBOX, so this matches typical usage.
 *
 * Classification:
 *  - Each message is classified by labelIds: SENT → sent, otherwise → received.
 *  - Self-sent emails (SENT + INBOX) appear in BOTH arrays.
 *  - Excludes SPAM, TRASH, DRAFT, CHAT, CATEGORY_FORUMS automatically.
 *
 * Handles large mailboxes (20k+) safely:
 *  - Exponential backoff with jitter on 429s (up to 5 retries)
 *  - Adaptive concurrency: starts at BATCH_SIZE, halves on rate-limit, recovers
 *  - Cooldown pause between batches to stay under Gmail quota
 */

import { BATCH_SIZE, RATE_LIMIT_DELAY } from '../constants';
import { gmailProxyBase, getAuthHeaders } from './backendClient';

/** Max retries per individual request before giving up */
const MAX_RETRIES = 5;

/** Pause between batches (ms) to stay under ~50 req/s Gmail quota */
const BATCH_COOLDOWN = 400;

/** Minimum concurrent requests (won't shrink below this) */
const MIN_BATCH = 5;

/** Labels to exclude entirely from results */
const EXCLUDE_LABELS = new Set(['SPAM', 'TRASH', 'DRAFT', 'CATEGORY_FORUMS', 'CHAT']);

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Expand `to` to end-of-day if it landed exactly on midnight, so the last day
 * of the range is inclusive. Returns ms-since-epoch for both bounds.
 */
function rangeBoundsMs(from, to) {
  const toEnd = new Date(to);
  if (toEnd.getHours() === 0 && toEnd.getMinutes() === 0 && toEnd.getSeconds() === 0) {
    toEnd.setHours(23, 59, 59, 999);
  }
  return { fromMs: from.getTime(), toMs: toEnd.getTime() };
}

function sleep(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function backoffMs(attempt) {
  const base = RATE_LIMIT_DELAY * Math.pow(2, attempt);
  const jitter = Math.random() * 1000;
  return Math.min(base + jitter, 60000);
}

// ── API calls with retry ───────────────────────────────────────────────────

async function fetchWithRetry(url) {
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const res = await fetch(url, { headers: getAuthHeaders(), cache: 'no-store' });
    if (res.ok) return res;

    // 503 is the proxy failing to verify team access — not worth retrying past
    // the normal budget, but it is transient, so it stays in the retry branch.
    if (res.status === 429 || res.status >= 500) {
      if (attempt === MAX_RETRIES) {
        throw new Error(`Gmail API error: ${res.status} after ${MAX_RETRIES} retries`);
      }
      await sleep(backoffMs(attempt));
      continue;
    }

    // 410 = the mailbox owner's Google grant is gone. Surface it in the shape
    // the team view already handles so it can prompt a reconnect.
    if (res.status === 410) {
      const err = new Error('Gmail connection expired — reconnect required');
      err.tokenExpired = true;
      err.status = 410;
      throw err;
    }

    const err = new Error(`Gmail API error: ${res.status}`);
    err.status = res.status;
    throw err;
  }
}

/** Append memberId only when reading someone else's mailbox. */
function withMember(params, memberId) {
  if (memberId) params.set('memberId', memberId);
  return params;
}

/**
 * List a single page of message IDs filtered by labelId.
 * Uses labelIds (not q=) because gmail.metadata scope rejects q=.
 */
async function fetchLabelPage(memberId, labelId, maxResults, pageToken = null) {
  const params = new URLSearchParams({ maxResults: String(maxResults), labelId });
  if (pageToken) params.set('pageToken', pageToken);
  withMember(params, memberId);

  const res = await fetchWithRetry(`${gmailProxyBase()}/messages?${params}`);

  const data = await res.json();
  return { messages: data.messages || [], nextPageToken: data.nextPageToken || null };
}

/**
 * Fetch full headers + labelIds for a single message.
 * labelIds are needed to classify sent vs received.
 */
async function fetchMessage(memberId, id) {
  // format=metadata and the header set are fixed server-side, so the Subject
  // header cannot be requested from here even by accident — privacy: the
  // product promises subjects are never read.
  const params = withMember(new URLSearchParams(), memberId);
  const qs = params.toString();
  const res = await fetchWithRetry(
    `${gmailProxyBase()}/messages/${encodeURIComponent(id)}${qs ? `?${qs}` : ''}`
  );
  return res.json();
}

/**
 * Page through one label, fetch metadata in small adaptive chunks, and keep
 * messages whose internalDate falls in [fromMs, toMs].
 *
 * Early-termination invariant: Gmail returns label pages in reverse-chrono
 * order. The moment any message in a chunk has internalDate < fromMs, every
 * subsequent ID (in this page AND in later pages) is even older — so we stop
 * fetching immediately instead of burning quota on out-of-range pages.
 *
 * Pushes kept messages into `accumulator.messages` and updates
 * `accumulator.fetched` so cross-label progress reporting stays monotonic.
 */
async function fetchByLabelInRange(memberId, labelId, fromMs, toMs, accumulator, onProgress) {
  let pageToken = null;
  let currentBatch = BATCH_SIZE;
  let done = false;

  while (!done) {
    const page = await fetchLabelPage(memberId, labelId, 500, pageToken);
    pageToken = page.nextPageToken;
    const pageIds = page.messages.map((m) => m.id);
    if (pageIds.length === 0) break;

    let i = 0;
    while (i < pageIds.length) {
      const chunkIds = pageIds.slice(i, i + currentBatch);

      let chunk;
      try {
        chunk = await Promise.all(chunkIds.map((id) => fetchMessage(memberId, id)));
      } catch (err) {
        if (err.message.includes('429') && currentBatch > MIN_BATCH) {
          currentBatch = Math.max(Math.floor(currentBatch / 2), MIN_BATCH);
          await sleep(backoffMs(1));
          continue; // retry same chunk with smaller concurrency
        }
        throw err;
      }

      // Ramp concurrency back toward BATCH_SIZE after a successful chunk.
      if (currentBatch < BATCH_SIZE) {
        currentBatch = Math.min(currentBatch + 2, BATCH_SIZE);
      }

      accumulator.fetched += chunk.length;
      i += chunk.length;

      let hitOld = false;
      for (const msg of chunk) {
        const internalMs = msg.internalDate ? Number(msg.internalDate) : NaN;
        if (Number.isNaN(internalMs)) {
          // No date — keep it; downstream parsing decides.
          accumulator.messages.push(msg);
          continue;
        }
        if (internalMs > toMs) continue;          // newer than range — skip
        if (internalMs < fromMs) {                 // older than range — stop after this chunk
          hitOld = true;
          continue;
        }
        accumulator.messages.push(msg);
      }

      if (onProgress) onProgress(accumulator.fetched, accumulator.fetched);

      if (hitOld) {
        // List is reverse-chrono; all later IDs in this page and later pages
        // are even older than fromMs.
        done = true;
        break;
      }

      if (i < pageIds.length) await sleep(BATCH_COOLDOWN);
    }

    if (!pageToken) done = true;
  }
}

/**
 * Main entry: fetch all email metadata for the given date range.
 *
 * Strategy (gmail.metadata scope, no q= allowed):
 *   1. Page INBOX in reverse-chrono, fetch metadata, keep messages in [from, to].
 *   2. Same for SENT.
 *   3. Dedupe (self-sent appears in both label lists).
 *   4. Classify by labelIds:
 *        - Has SENT label → sent
 *        - No SENT label  → received
 *        - SENT + INBOX/CATEGORY_/UNREAD/Label_ → both (self-sent)
 *
 * @param {string} token
 * @param {Date} from
 * @param {Date} to
 * @param {function} onProgress
 * @returns {Promise<{received: RawMessage[], sent: RawMessage[]}>}
 */
export async function fetchEmailMetadata(memberId, from, to, onProgress) {
  const { fromMs, toMs } = rangeBoundsMs(from, to);
  if (onProgress) onProgress(0, 0);

  const accumulator = { messages: [], fetched: 0 };

  // INBOX first (received), then SENT — sequential keeps progress monotonic.
  await fetchByLabelInRange(memberId, 'INBOX', fromMs, toMs, accumulator, onProgress);
  await fetchByLabelInRange(memberId, 'SENT', fromMs, toMs, accumulator, onProgress);

  // Dedupe by id (self-sent appears in both INBOX and SENT pages).
  const byId = new Map();
  for (const m of accumulator.messages) byId.set(m.id, m);

  const received = [];
  const sent = [];

  for (const msg of byId.values()) {
    const labels = new Set(msg.labelIds || []);

    // Skip excluded labels (SPAM, TRASH, DRAFT, CHAT, CATEGORY_FORUMS)
    if ([...labels].some((l) => EXCLUDE_LABELS.has(l))) continue;

    const isSent = labels.has('SENT');

    if (isSent) {
      sent.push(msg);

      // Self-sent: has SENT label AND at least one received-side label.
      const hasReceivedLabel = labels.has('INBOX') || labels.has('UNREAD') ||
        [...labels].some((l) => l.startsWith('CATEGORY_') || l.startsWith('Label_'));
      if (hasReceivedLabel) {
        received.push(msg);
      }
    } else {
      // No SENT label = received email
      received.push(msg);
    }
  }

  return { received, sent };
}

/**
 * Decode a base64url string (Gmail's body encoding) to UTF-8 text.
 * Only used when the proxy returned format=full (Ergode).
 */
function decodeBase64Url(data) {
  if (!data) return '';
  const padded = data.replace(/-/g, '+').replace(/_/g, '/');
  try {
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return '';
  }
}

function extractBodyParts(payload, found = { plain: '', html: '' }) {
  if (!payload) return found;
  const mime = payload.mimeType || '';
  const data = payload.body?.data;

  if (data) {
    if (mime === 'text/plain' && !found.plain) {
      found.plain = decodeBase64Url(data);
    } else if (mime === 'text/html' && !found.html) {
      found.html = decodeBase64Url(data);
    }
  }

  if (Array.isArray(payload.parts)) {
    for (const part of payload.parts) {
      extractBodyParts(part, found);
      if (found.plain && found.html) break;
    }
  }
  return found;
}

function stripHtml(html = '') {
  return html.replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Parse a raw Gmail message into a normalized object.
 * Subject/body are filled only when the proxy returned them (Ergode full mode).
 */
export function parseMessage(raw, direction) {
  const headers = {};
  (raw.payload?.headers || []).forEach((h) => {
    headers[h.name.toLowerCase()] = h.value;
  });

  const parts = extractBodyParts(raw.payload);
  const body = parts.plain || (parts.html ? stripHtml(parts.html) : '');

  return {
    id: raw.id,
    direction,
    from: headers['from'] || '',
    to: headers['to'] || '',
    subject: headers['subject'] || '',
    snippet: raw.snippet || '',
    body,
    date: headers['date'] ? new Date(headers['date']) : null,
    inReplyTo: headers['in-reply-to'] || null,
    messageId: headers['message-id'] || raw.id,
    threadId: raw.threadId,
  };
}
